import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
interface IncomeCalculatorProps {
  isOpen: boolean;
  onClose: () => void;
}
export function IncomeCalculator({
  isOpen,
  onClose
}: IncomeCalculatorProps) {
  const [contacts, setContacts] = useState(10);
  const [closeRate, setCloseRate] = useState(30);
  const [price, setPrice] = useState(350);
  const [recurring, setRecurring] = useState(false);
  const [bump, setBump] = useState(false);
  // Calculate results
  const closed = Math.round(contacts * (closeRate / 100));
  const sitesIncome = closed * price;
  const bumpIncome = bump ? closed * 17 : 0;
  const weeklyIncome = sitesIncome + bumpIncome;
  const monthlyIncome = weeklyIncome * 4;
  const recurringMonth = recurring ? closed * 4 * 99 : 0;
  const yearlyIncome = monthlyIncome * 12 + recurringMonth * 12;
  const fmt = (n: number) => {
    return '$' + Math.round(n).toLocaleString('en-US');
  };
  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);
  return <AnimatePresence data-id="element-187">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100]" data-id="element-188" />

          {/* Modal */}
          <div className="fixed inset-0 z-[101] overflow-y-auto" data-id="element-189">
            <div className="min-h-full flex items-center justify-center p-4" data-id="element-190">
              <motion.div initial={{
            opacity: 0,
            scale: 0.95,
            y: 20
          }} animate={{
            opacity: 1,
            scale: 1,
            y: 0
          }} exit={{
            opacity: 0,
            scale: 0.95,
            y: 20
          }} transition={{
            duration: 0.3
          }} className="relative w-full max-w-4xl bg-navy border border-gold/30 rounded-2xl shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-191">
                {/* Close button */}
                <button onClick={onClose} className="absolute top-4 right-4 z-10 p-2 rounded-lg bg-navy-dark/50 hover:bg-navy-dark border border-gold/20 hover:border-gold/40 transition-colors" aria-label="Cerrar calculadora" data-id="element-192">
                  <X className="h-5 w-5 text-gray-300" data-id="element-193" />
                </button>

                {/* Content */}
                <div className="p-6 md:p-8 max-h-[85vh] overflow-y-auto" data-id="element-194">
                  {/* Header */}
                  <div className="text-center mb-8" data-id="element-195">
                    <h3 className="font-heading font-black text-3xl md:text-4xl mb-2" data-id="element-196">
                      ¿Cuánto Puedes Ganar{' '}
                      <span className="text-gold" data-id="element-197">Esta Semana?</span>
                    </h3>
                    <p className="font-body text-sm text-gray-400" data-id="element-198">
                      Mueve los controles y calcula tu ingreso real con el
                      sistema AI Web Closer
                    </p>
                  </div>

                  {/* Calculator Card */}
                  <div className="bg-navy-dark border border-gold/20 rounded-xl p-6 md:p-8 mb-6" data-id="element-199">
                    <div className="font-heading font-bold text-xs tracking-widest text-gold uppercase mb-6" data-id="element-200">
                      Calculadora de Ingresos
                    </div>

                    {/* Slider 1: Contacts */}
                    <div className="mb-6" data-id="element-201">
                      <div className="flex justify-between items-center mb-2" data-id="element-202">
                        <span className="font-body text-sm text-gray-300" data-id="element-203">
                          Negocios que contactas por semana
                        </span>
                        <div className="font-heading font-bold text-lg text-gold bg-gold/10 border border-gold/30 rounded-lg px-3 py-1 min-w-[60px] text-center" data-id="element-204">
                          {contacts}
                        </div>
                      </div>
                      <input type="range" min="1" max="50" value={contacts} onChange={e => setContacts(Number(e.target.value))} className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-gold" data-id="element-205" />
                    </div>

                    {/* Slider 2: Close Rate */}
                    <div className="mb-6" data-id="element-206">
                      <div className="flex justify-between items-center mb-2" data-id="element-207">
                        <span className="font-body text-sm text-gray-300" data-id="element-208">
                          Tasa de cierre (% que dice SÍ)
                        </span>
                        <div className="font-heading font-bold text-lg text-gold bg-gold/10 border border-gold/30 rounded-lg px-3 py-1 min-w-[60px] text-center" data-id="element-209">
                          {closeRate}
                          <span className="text-xs" data-id="element-210">%</span>
                        </div>
                      </div>
                      <input type="range" min="5" max="60" value={closeRate} onChange={e => setCloseRate(Number(e.target.value))} className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-gold" data-id="element-211" />
                    </div>

                    {/* Slider 3: Price */}
                    <div className="mb-8" data-id="element-212">
                      <div className="flex justify-between items-center mb-2" data-id="element-213">
                        <span className="font-body text-sm text-gray-300" data-id="element-214">
                          Precio por sitio web
                        </span>
                        <div className="font-heading font-bold text-lg text-gold bg-gold/10 border border-gold/30 rounded-lg px-3 py-1 min-w-[70px] text-center" data-id="element-215">
                          ${price}
                        </div>
                      </div>
                      <input type="range" min="150" max="1500" step="50" value={price} onChange={e => setPrice(Number(e.target.value))} className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-gold" data-id="element-216" />
                    </div>

                    {/* Divider */}
                    <div className="relative mb-6" data-id="element-217">
                      <div className="absolute inset-0 flex items-center" data-id="element-218">
                        <div className="w-full border-t border-gold/20" data-id="element-219"></div>
                      </div>
                    </div>

                    {/* Toggle: Recurring */}
                    <div className="flex items-center gap-3 mb-4 p-3 bg-navy-light/50 rounded-lg border border-gold/10" data-id="element-220">
                      <span className="font-body text-sm text-gray-300 flex-1" data-id="element-221">
                        Añadir paquete de mantenimiento mensual ($99/cliente)
                      </span>
                      <label className="relative inline-flex items-center cursor-pointer" data-id="element-222">
                        <input type="checkbox" checked={recurring} onChange={e => setRecurring(e.target.checked)} className="sr-only peer" data-id="element-223" />
                        <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gold" data-id="element-224"></div>
                      </label>
                      <div className="font-heading font-bold text-xs text-navy bg-gold rounded px-2 py-1" data-id="element-225">
                        +$99/mes
                      </div>
                    </div>

                    {/* Toggle: Bump */}
                    <div className="flex items-center gap-3 mb-8 p-3 bg-navy-light/50 rounded-lg border border-gold/10" data-id="element-226">
                      <span className="font-body text-sm text-gray-300 flex-1" data-id="element-227">
                        Incluir upsell Kit del Primer Cliente ($17)
                      </span>
                      <label className="relative inline-flex items-center cursor-pointer" data-id="element-228">
                        <input type="checkbox" checked={bump} onChange={e => setBump(e.target.checked)} className="sr-only peer" data-id="element-229" />
                        <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gold" data-id="element-230"></div>
                      </label>
                      <div className="font-heading font-bold text-xs text-navy bg-gold rounded px-2 py-1" data-id="element-231">
                        +$17
                      </div>
                    </div>

                    {/* Breakdown */}
                    <div className="pt-2" data-id="element-232">
                      <div className="font-heading text-xs font-bold tracking-widest text-gray-400 uppercase mb-4" data-id="element-233">
                        Desglose Semanal
                      </div>
                      <div className="space-y-2" data-id="element-234">
                        <div className="flex justify-between items-center py-2 border-b border-gray-700/50" data-id="element-235">
                          <span className="font-body text-sm text-gray-400" data-id="element-236">
                            Negocios contactados
                          </span>
                          <span className="font-heading font-bold text-white" data-id="element-237">
                            {contacts}
                          </span>
                        </div>
                        <div className="flex justify-between items-center py-2 border-b border-gray-700/50" data-id="element-238">
                          <span className="font-body text-sm text-gray-400" data-id="element-239">
                            Clientes cerrados
                          </span>
                          <span className="font-heading font-bold text-white" data-id="element-240">
                            {closed}
                          </span>
                        </div>
                        <div className="flex justify-between items-center py-2 border-b border-gray-700/50" data-id="element-241">
                          <span className="font-body text-sm text-gray-400" data-id="element-242">
                            Ingreso por sitios web
                          </span>
                          <span className="font-heading font-bold text-gold" data-id="element-243">
                            {fmt(sitesIncome)}
                          </span>
                        </div>
                        <div className="flex justify-between items-center py-2 border-b border-gray-700/50" data-id="element-244">
                          <span className="font-body text-sm text-gray-400" data-id="element-245">
                            Ingreso recurrente (mes 1)
                          </span>
                          <span className="font-heading font-bold text-gold" data-id="element-246">
                            {recurring ? `${fmt(closed * 99)}/sem` : '$0'}
                          </span>
                        </div>
                        <div className="flex justify-between items-center py-3 border-t border-gold/20 mt-2" data-id="element-247">
                          <span className="font-heading font-bold text-white" data-id="element-248">
                            Total semana 1
                          </span>
                          <span className="font-heading font-black text-xl text-gold" data-id="element-249">
                            {fmt(weeklyIncome)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Pricing Reference */}
                  <div className="bg-navy-dark border border-gold/20 rounded-xl p-6 md:p-8 mb-6" data-id="element-250">
                    <div className="font-heading font-bold text-xs tracking-widest text-gold uppercase mb-6" data-id="element-251">
                      💰 Precios de Referencia por Nicho
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-id="element-252">
                      <div className="bg-navy-light/50 border border-gray-700 rounded-lg p-4 text-center" data-id="element-253">
                        <div className="font-body text-sm font-semibold text-gray-300 mb-2" data-id="element-254">
                          🍕 Restaurantes
                        </div>
                        <div className="font-heading font-black text-lg text-gold" data-id="element-255">
                          $300–$500
                        </div>
                        <div className="font-body text-xs text-gray-500 mt-1" data-id="element-256">
                          Alta urgencia
                        </div>
                      </div>
                      <div className="bg-gold/10 border-2 border-gold rounded-lg p-4 text-center relative" data-id="element-257">
                        <div className="absolute -top-2 left-1/2 -translate-x-1/2 bg-gold text-navy text-[10px] font-black px-2 py-0.5 rounded-full whitespace-nowrap" data-id="element-258">
                          ⭐ RECOMENDADO
                        </div>
                        <div className="font-body text-sm font-semibold text-gray-300 mb-2" data-id="element-259">
                          🦷 Dentistas
                        </div>
                        <div className="font-heading font-black text-lg text-gold" data-id="element-260">
                          $500–$900
                        </div>
                        <div className="font-body text-xs text-gray-500 mt-1" data-id="element-261">
                          Mayor ticket
                        </div>
                      </div>
                      <div className="bg-navy-light/50 border border-gray-700 rounded-lg p-4 text-center" data-id="element-262">
                        <div className="font-body text-sm font-semibold text-gray-300 mb-2" data-id="element-263">
                          🔧 Talleres
                        </div>
                        <div className="font-heading font-black text-lg text-gold" data-id="element-264">
                          $250–$450
                        </div>
                        <div className="font-body text-xs text-gray-500 mt-1" data-id="element-265">
                          Fácil de cerrar
                        </div>
                      </div>
                      <div className="bg-gold/10 border-2 border-gold rounded-lg p-4 text-center relative" data-id="element-266">
                        <div className="absolute -top-2 left-1/2 -translate-x-1/2 bg-gold text-navy text-[10px] font-black px-2 py-0.5 rounded-full whitespace-nowrap" data-id="element-267">
                          ⭐ RECOMENDADO
                        </div>
                        <div className="font-body text-sm font-semibold text-gray-300 mb-2" data-id="element-268">
                          ⚖️ Abogados
                        </div>
                        <div className="font-heading font-black text-lg text-gold" data-id="element-269">
                          $700–$1,500
                        </div>
                        <div className="font-body text-xs text-gray-500 mt-1" data-id="element-270">
                          Máximo ticket
                        </div>
                      </div>
                      <div className="bg-navy-light/50 border border-gray-700 rounded-lg p-4 text-center" data-id="element-271">
                        <div className="font-body text-sm font-semibold text-gray-300 mb-2" data-id="element-272">
                          ✂️ Peluquerías
                        </div>
                        <div className="font-heading font-black text-lg text-gold" data-id="element-273">
                          $200–$350
                        </div>
                        <div className="font-body text-xs text-gray-500 mt-1" data-id="element-274">
                          Volumen alto
                        </div>
                      </div>
                      <div className="bg-navy-light/50 border border-gray-700 rounded-lg p-4 text-center" data-id="element-275">
                        <div className="font-body text-sm font-semibold text-gray-300 mb-2" data-id="element-276">
                          🏋️ Gimnasios
                        </div>
                        <div className="font-heading font-black text-lg text-gold" data-id="element-277">
                          $350–$600
                        </div>
                        <div className="font-body text-xs text-gray-500 mt-1" data-id="element-278">
                          Buen margen
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </>}
    </AnimatePresence>;
}